/**
 * React hooks for the conversations store (iter135.11 — Phase A).
 *
 * Each hook subscribes to ONE slice via ``useSyncExternalStore`` so
 * components only re-render when their slice's version counter
 * bumps. The store's snapshot getters return the SAME reference
 * until that slice changes — that's the whole point.
 */
import { useSyncExternalStore } from "react";
import { convStore } from "./store";

/** Sidebar list — re-renders only when convs/order changes. */
export function useConvList() {
  return useSyncExternalStore(convStore.subscribe, convStore.getConvList);
}

/** Single conv metadata — re-renders only when this conv's row
 *  changes. (We use the same convs slice version; the cost is one
 *  Map.get per consumer per render, which is negligible.)
 */
export function useConv(convId) {
  return useSyncExternalStore(
    convStore.subscribe,
    () => convStore.getConv(convId),
  );
}

/** Ordered list of message_ids for ``convId``. Stable reference
 *  until that conv's messages mutate.
 */
export function useMessageOrder(convId) {
  return useSyncExternalStore(
    convStore.subscribe,
    () => convStore.getMessageOrder(convId),
  );
}

/** Single message by id. The MessageBubble subscribes to this so
 *  ONLY the edited / recalled bubble re-renders, not the whole list.
 */
export function useMessage(convId, msgId) {
  return useSyncExternalStore(
    convStore.subscribe,
    () => convStore.getMessage(convId, msgId),
  );
}

export function useTypingUsers(convId) {
  return useSyncExternalStore(
    convStore.subscribe,
    () => convStore.getTypingList(convId),
  );
}

/** Map<userKey, lastSeq>. The MessageBubble can compute its own
 *  read-by-N by walking this map for entries with seq >= its own
 *  sequence_number.
 */
export function useReadsByUser(convId) {
  return useSyncExternalStore(
    convStore.subscribe,
    () => convStore.getReadsByUser(convId),
  );
}

export function useSelectedConvId() {
  return useSyncExternalStore(
    convStore.subscribe,
    convStore.getSelectedConvId,
  );
}
